import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Alert,
  Skeleton,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SwalReact from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { updateFrameworkVisibility } from 'services/transparency-config-service';
import { getFrameworksInUse } from 'services/gap-analysis';
import { DotsLoader } from 'Components/DotsLoader';

const MySwal = SwalReact(Swal);

/**
 * Framework Visibility Configuration
 * Configure which frameworks are visible in the transparency center
 */
export const FrameworkVisibility = ({ config, onUpdate, companyKey, companyId }) => {
  const { t } = useTranslation(['transparency', 'common']);

  // Fetch frameworks in use by company from gap analysis service
  const { data: frameworksData, isLoading: loadingFrameworks, error: frameworksError } = useQuery(
    ['frameworksInUse', companyId],
    () => getFrameworksInUse(companyId),
    {
      enabled: !!companyId,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  // Initialize form with enriched data
  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      frameworks: [],
    },
  });

  const { fields, move } = useFieldArray({
    control,
    name: 'frameworks',
  });

  // Enrich frameworks with saved config
  useEffect(() => {
    if (frameworksData?.data && Array.isArray(frameworksData.data)) {
      // Defensive: Ensure frameworkVisibility is an array
      const savedConfig = Array.isArray(config?.frameworkVisibility)
        ? config.frameworkVisibility
        : [];

      const enriched = frameworksData.data.map((fw, index) => {
        const existing = savedConfig.find(s => s.frameworkId === fw._id);
        return {
          frameworkId: fw._id,
          frameworkName: fw.name,
          showBadgePublicly: existing?.showBadgePublicly ?? false,
          showMaturityProtected: existing?.showMaturityProtected ?? false,
          displayOrder: existing?.displayOrder ?? index,
        };
      }).sort((a, b) => a.displayOrder - b.displayOrder);

      setValue('frameworks', enriched);
    }
  }, [frameworksData, config, setValue]);

  // Save mutation
  const mutation = useMutation(
    (data) => updateFrameworkVisibility(companyKey, data.frameworks),
    {
      onSuccess: () => {
        MySwal.fire({
          title: t('transparency:frameworkVisibility.notifications.success'),
          text: t('transparency:frameworkVisibility.notifications.successText'),
          icon: 'success',
        });
        onUpdate();
      },
      onError: (error) => {
        MySwal.fire({
          title: t('transparency:frameworkVisibility.notifications.error'),
          text: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
    }
  );

  const onSubmit = (data) => {
    // Update display order before saving
    const frameworks = data.frameworks.map((fw, index) => ({
      ...fw,
      displayOrder: index,
    }));
    mutation.mutate({ frameworks });
  };

  // Move framework up in display order
  const moveUp = (index) => {
    if (index > 0) {
      move(index, index - 1);
    }
  };

  // Move framework down in display order
  const moveDown = (index) => {
    if (index < fields.length - 1) {
      move(index, index + 1);
    }
  };

  // Loading state
  if (loadingFrameworks) {
    return (
      <Paper sx={{ p: 3 }}>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Paper>
    );
  }

  // Error state
  if (frameworksError) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">
          {t('transparency:frameworkVisibility.error')}
        </Alert>
      </Paper>
    );
  }

  // Empty state
  if (!frameworksData?.data || frameworksData.data.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('transparency:frameworkVisibility.title')}
        </Typography>
        <Alert severity="info">
          {t('transparency:frameworkVisibility.empty')}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h6" gutterBottom>
          {t('transparency:frameworkVisibility.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('transparency:frameworkVisibility.subtitle')}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('transparency:frameworkVisibility.table.framework')}</TableCell>
                <TableCell align="center">
                  <Box>
                    <Typography variant="subtitle2">{t('transparency:frameworkVisibility.table.showBadge')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('transparency:frameworkVisibility.table.badgeLevel')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box>
                    <Typography variant="subtitle2">{t('transparency:frameworkVisibility.table.showMaturity')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('transparency:frameworkVisibility.table.maturityLevel')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">{t('transparency:frameworkVisibility.table.order')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((field, index) => (
                <TableRow key={field.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {field.frameworkName}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Controller
                      control={control}
                      name={`frameworks.${index}.showBadgePublicly`}
                      render={({ field: { onChange, value } }) => (
                        <Checkbox
                          checked={value}
                          onChange={onChange}
                          color="primary"
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Controller
                      control={control}
                      name={`frameworks.${index}.showMaturityProtected`}
                      render={({ field: { onChange, value } }) => (
                        <Checkbox
                          checked={value}
                          onChange={onChange}
                          color="primary"
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => moveUp(index)}
                        disabled={index === 0}
                        aria-label={t('transparency:frameworkVisibility.moveUp')}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => moveDown(index)}
                        disabled={index === fields.length - 1}
                        aria-label={t('transparency:frameworkVisibility.moveDown')}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? <DotsLoader /> : t('common:buttons.save')}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};
